/*
The store behind the C-12 save-status popup (XOL-596 UX addendum, 2026-10-13;
components/requests/SaveStatusPopup.vue renders it).

ONE card, app-wide. It shows the whole save cycle (Changes pending -> Saving… ->
Saved) from `requestRowSync.syncState.activity`, and every XOL-596 notice as a
SECTION of the same card:

    notify(opts)       an error / warning / info section (replaces `$notify`)
    success(text)      "Saved: …" for 3 s (replaces the success `$message`)
    leaving([text])    "Saving your changes…" (or `text`) while a flush point waits
    setRetry(info)     the retry strip of a blocked close (saveWait.keepTrying)
    resolveNetwork()   a later successful save (or the end of the retry loop)
                       removes the "connection problem" section
    dismiss(id)        the section's Dismiss

This module holds data and the announcements the two live regions speak; it
imports no Vue component, so lib/syncNotices, lib/saveWait, lib/leaveGuard and the
mixins can all use it. The card derives its head from the store plus `activity`
(`cycleOf`, `headOf`).

Section: { id, type: 'warning' | 'error' | 'info', kind, title, lead, lines, groups,
footer, savedLine, keys, outcomes, retry, onRetry, onDismiss, duration }. `kind`
'network' marks the one "connection problem" section; its `keys` / `outcomes` say
which fields it lists, so a later successful save of all of them resolves it.

ES5, no optional chaining or nullish coalescing.
------------------------------------------------------------------------ */

import Vue from 'vue'
import { copyText } from '@/lib/requestSyncCopy'

// How long "Saved" stays.
export var SAVED_MS = 3000
// A save outside any drawer scope (an EditPane Save) shows "Saving…" only if it
// takes longer than this.
export var LOOSE_SAVING_MS = 300
// "Saving…" is announced only if the save is still going after this long.
export var SAVING_ANNOUNCE_MS = 1000

export var state = Vue.observable({
  sections: [],
  // { text, id } while a Saved shows (3 s), else null
  saved: null,
  // how many flush points are waiting ("Saving your changes…")
  leaving: 0,
  // What each waiting flush point says ('' = the default "Saving your changes…");
  // the newest one speaks. A drawer close that is locking approvals says
  // "Locking the approval…" (XOL-596 approval lock).
  leavingTexts: [],
  // { attempt, nextAt, running } while a blocked close retries, else null
  retry: null,
  // The live regions: bump `n` and set `text`; the card clears and re-sets them
  // on the next tick so a repeated text is spoken again.
  polite: { n: 0, text: '' },
  assertive: { n: 0, text: '' }
})

var sectionCounter = 0
var savedCounter = 0
var savedTimer = null
var sectionTimers = {}
var retryNow = null

function announce (text, loud) {
  var r = loud ? state.assertive : state.polite
  r.text = text
  r.n++
}

/** Speak `text` in the polite (status) or, with `loud`, the assertive (alert) region. */
export function say (text, loud) {
  announce(text, loud)
}

function findIndex (id) {
  for (var i = 0; i < state.sections.length; i++) {
    if (state.sections[i].id === id) {
      return i
    }
  }
  return -1
}

function clearSectionTimer (id) {
  if (sectionTimers[id]) {
    clearTimeout(sectionTimers[id])
    delete sectionTimers[id]
  }
}

function plain (section) {
  return section.title + (section.lead ? '. ' + section.lead : '')
}

/**
 * Add a section (newest first) and announce it. `opts` is what the `$notify`
 * calls passed: { type, title, message (a string; becomes the lead), duration },
 * plus the C-1 fields { lead, groups, footer, savedLine } and, for the network
 * section, { kind: 'network', keys, outcomes, retry, onRetry }. A second network
 * section updates the first in place (and is not announced again).
 *
 * @return {Object} the section
 */
export function notify (opts) {
  opts = opts || {}

  if (opts.kind === 'network') {
    var existing = networkSection()
    if (existing) {
      update(existing.id, opts)
      return existing
    }
  }

  // A repeat refusal of the same thing (XOL-596 completed steps: `replaceKey`) replaces
  // its section instead of adding a second one.
  if (opts.replaceKey) {
    state.sections.filter(function (s) { return s.replaceKey === opts.replaceKey }).forEach(function (s) {
      remove(s.id)
    })
  }

  var section = {
    replaceKey: opts.replaceKey || null,
    id: ++sectionCounter,
    type: opts.type === 'error' || opts.type === 'info' ? opts.type : 'warning',
    kind: opts.kind || null,
    title: opts.title || '',
    lead: opts.lead !== undefined && opts.lead !== null ? opts.lead : (typeof opts.message === 'string' ? opts.message : null),
    lines: opts.lines || [],
    groups: opts.groups || [],
    footer: opts.footer || null,
    savedLine: opts.savedLine || null,
    keys: opts.keys || [],
    outcomes: opts.outcomes || [],
    retry: !!opts.retry,
    onRetry: typeof opts.onRetry === 'function' ? opts.onRetry : null,
    onDismiss: typeof opts.onDismiss === 'function' ? opts.onDismiss : null,
    duration: typeof opts.duration === 'number' ? opts.duration : 0
  }

  state.sections.unshift(section)

  if (section.duration > 0) {
    sectionTimers[section.id] = setTimeout(function () {
      remove(section.id)
    }, section.duration)
  }

  announce(plain(section), section.type !== 'info')

  return section
}

/** Replace the fields of a section in place (same id, same position). */
export function update (id, opts) {
  var i = findIndex(id)
  if (i === -1) {
    return null
  }
  var s = state.sections[i]
  ;['title', 'lead', 'lines', 'groups', 'footer', 'savedLine', 'keys', 'outcomes'].forEach(function (k) {
    if (opts[k] !== undefined) {
      s[k] = opts[k]
    }
  })
  if (opts.type) {
    s.type = opts.type
  }
  if (typeof opts.onRetry === 'function') {
    s.onRetry = opts.onRetry
  }
  return s
}

/** Remove a section without running its Dismiss handler. */
export function remove (id) {
  var i = findIndex(id)
  clearSectionTimer(id)
  if (i !== -1) {
    state.sections.splice(i, 1)
  }
}

/** The section's Dismiss: remove it, then run its handler (clears the stash). */
export function dismiss (id) {
  var i = findIndex(id)
  if (i === -1) {
    return
  }
  var handler = state.sections[i].onDismiss
  remove(id)
  if (handler) {
    handler()
  }
}

/** The one "connection problem" section, or null. */
export function networkSection () {
  for (var i = 0; i < state.sections.length; i++) {
    if (state.sections[i].kind === 'network') {
      return state.sections[i]
    }
  }
  return null
}

/**
 * The connection problem is over (a retry saved, the close was given up, or the
 * loop ended): remove the network section. Fields are removed by `keys` first
 * (a later burst saved some of them); the section goes when none are left.
 * Returns true when the section was removed.
 */
export function resolveNetwork (savedKeys) {
  var section = networkSection()
  if (!section) {
    return false
  }
  if (savedKeys) {
    var left = section.outcomes.filter(function (o) {
      return savedKeys.indexOf(keyOf(o)) === -1
    })
    if (left.length > 0) {
      section.outcomes = left
      section.keys = left.map(keyOf)
      return false
    }
  }
  remove(section.id)
  return true
}

/** The key of a field outcome: kind:id:path. */
export function keyOf (o) {
  return o.kind + ':' + o.id + ':' + o.path
}

/** "Saved: …" for 3 s, announced once. */
export function success (text) {
  var id = ++savedCounter
  state.saved = { text: text, id: id }
  clearTimeout(savedTimer)
  savedTimer = setTimeout(function () {
    savedTimer = null
    if (state.saved && state.saved.id === id) {
      state.saved = null
    }
  }, SAVED_MS)
  announce(text, false)
}

/**
 * A flush point is waiting on saves (drawer close, navigation, logout): the card
 * reads "Saving your changes…" at once, or `text` ("Locking the approval…" while a
 * close locks approvals). Returns the function that ends it.
 */
export function leaving (text) {
  var wasIdle = state.leaving === 0
  var said = text || copyText('requestSync.leave.saving')
  var entry = { text: text || '' }
  state.leaving++
  state.leavingTexts.push(entry)
  if (wasIdle || text) {
    announce(said, false)
  }
  var ended = false
  return function end () {
    if (!ended) {
      ended = true
      state.leaving = Math.max(0, state.leaving - 1)
      var at = state.leavingTexts.indexOf(entry)
      if (at !== -1) {
        state.leavingTexts.splice(at, 1)
      }
    }
  }
}

/** What the card says while a flush point waits: the newest text, else the default. */
export function leavingText () {
  for (var i = state.leavingTexts.length - 1; i >= 0; i--) {
    if (state.leavingTexts[i].text) {
      return state.leavingTexts[i].text
    }
  }
  return copyText('requestSync.leave.saving')
}

/**
 * The retry loop of a blocked close publishes where it is: { attempt, nextAt,
 * running }, with the function that runs the next attempt now (Try again skips
 * the countdown). `null` clears it.
 */
export function setRetry (info, now) {
  state.retry = info ? { attempt: info.attempt, nextAt: info.nextAt || null, running: !!info.running } : null
  retryNow = info && typeof now === 'function' ? now : null
}

/** Try again skips the retry countdown when a loop is waiting; false if none is. */
export function retryNowIfWaiting () {
  if (state.retry && !state.retry.running && retryNow) {
    retryNow()
    return true
  }
  return false
}

/** Forget everything (requestRowSync.resetSyncState calls it, so the session reset hook covers it). */
export function reset () {
  state.sections.splice(0, state.sections.length)
  state.saved = null
  state.leaving = 0
  state.leavingTexts.splice(0, state.leavingTexts.length)
  state.retry = null
  state.polite.text = ''
  state.assertive.text = ''
  clearTimeout(savedTimer)
  savedTimer = null
  Object.keys(sectionTimers).forEach(function (id) {
    clearTimeout(sectionTimers[id])
  })
  sectionTimers = {}
  retryNow = null
}

/* ------------------------------------------------------------------------
   The derived head, pure (the card and the specs share it)
------------------------------------------------------------------------ */

/**
 * The cycle the form is in: 'leaving' | 'saving' | 'pending' | null. `activity` is
 * requestRowSync.syncState.activity; `blocked` (a 428 was seen) hides every cycle.
 */
export function cycleOf (activity, leavingCount, blocked, looseSavingShown) {
  if (blocked) {
    return null
  }
  if (leavingCount > 0) {
    return 'leaving'
  }
  if (activity.savingScoped || (activity.saving && looseSavingShown)) {
    return 'saving'
  }
  if (activity.pending) {
    return 'pending'
  }
  return null
}
